import { Listbox, Portal } from "@headlessui/react";
import Checkbox from "components/Checkbox";
import { mdiChevronDown } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { getClassByVariant } from "helpers/getClassByVariant";
import type { IOption } from "models/Form";
import styles from "./style.module.scss";
import ScrollHint from "../../hooks/ScrollHint";
import { useColorScheme } from "../../hooks/useColorScheme";

const MIN_AVAILABLE_SPACE = 160;
const PORTAL_Z_INDEX = 9999;

type Direction = "up" | "down";

interface IProps {
  id?: string;
  value: IOption[];
  onChange: (value: IOption[]) => void;
  onDropdownClose?: (value: IOption[]) => void;
  options: Array<IOption>;
  label?: string | React.ReactNode;
  className?: string;
  labelClass?: string;
  btnClass?: string;
  inputClass?: string;
  arrowClass?: string;
  dropdownClass?: string;
  dropdownContainerClass?: string;
  dropdownMaxHeight?: number;
  usePortal?: boolean;
  color?: "primary" | "secondary";
  direction?: Direction;
  placeholder?: string;
  disabled?: boolean;
  style?: Record<any, any>;
}

export default function MultiSelect({
  id,
  options,
  value = [],
  onChange,
  onDropdownClose,
  label,
  className,
  color = "primary",
  labelClass,
  btnClass,
  inputClass,
  arrowClass,
  dropdownClass,
  dropdownContainerClass = "",
  dropdownMaxHeight = 300,
  usePortal = true,
  direction = "down",
  placeholder,
  disabled = false,
  style
}: Readonly<IProps>) {
  const placeholderLabel = useMemo(() => placeholder || "Select option(s)", [placeholder]);
  const selectionSummary = useMemo(
    () => (value.length ? value.map((item) => item.label).join(", ") : placeholderLabel),
    [placeholderLabel, value]
  );
  const wasDropdownOpenRef = useRef(false);
  const [initialValues, setInitialValues] = useState<IOption[]>([]);

  const { currentIsDark } = useColorScheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuState, setMenuState] = useState({
    left: 0,
    top: 0,
    width: 0,
    direction,
    maxHeight: dropdownMaxHeight
  });

  const updateMenuMetrics = useCallback(() => {
    if (typeof window === "undefined" || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const scrollY = window.scrollY ?? window.pageYOffset ?? 0;
    const scrollX = window.scrollX ?? window.pageXOffset ?? 0;
    const viewportHeight = window.innerHeight;

    const availableBelow = viewportHeight - rect.bottom;
    const availableAbove = rect.top;

    let resolvedDirection: Direction = direction;
    if (
      direction === "down" &&
      availableBelow < MIN_AVAILABLE_SPACE &&
      availableAbove > availableBelow
    ) {
      resolvedDirection = "up";
    } else if (
      direction === "up" &&
      availableAbove < MIN_AVAILABLE_SPACE &&
      availableBelow > availableAbove
    ) {
      resolvedDirection = "down";
    }

    const computedMaxHeight =
      resolvedDirection === "down"
        ? Math.max(80, Math.min(dropdownMaxHeight, availableBelow - 16))
        : Math.max(80, Math.min(dropdownMaxHeight, availableAbove - 16));

    const left = Math.min(rect.left, window.innerWidth - rect.width);

    setMenuState({
      left: left + scrollX,
      top: (resolvedDirection === "down" ? rect.bottom : rect.top) + scrollY,
      width: rect.width,
      direction: resolvedDirection,
      maxHeight: computedMaxHeight
    });
  }, [direction, dropdownMaxHeight]);

  useLayoutEffect(() => {
    updateMenuMetrics();
  }, [updateMenuMetrics, value.length]);

  const listWrapperClasses = useMemo(
    () =>
      clsx(
        "rounded-lg border border-card shadow-lg text-base space-y-1 !bg-input",
        currentIsDark && "border border-gray-600 text-gray-100",
        dropdownContainerClass
      ),
    [currentIsDark, dropdownContainerClass]
  );

  const optionClasses = useMemo(
    () =>
      clsx(
        "block rounded-md px-3 py-2 my-0.5 text-base leading-tight transition-colors duration-150",
        dropdownClass
      ),
    [dropdownClass]
  );

  // Helper function to stringify options for comparison
  const stringifyOptions = (options: IOption[]) =>
    [...options]
      .sort((a, b) => String(a.id).localeCompare(String(b.id)))
      .map((opt) => opt.id)
      .join(",");

  const renderOptions = () => (
    <Listbox.Options
      static
      className={clsx(
        "nested-scroll-listbox",
        listWrapperClasses,
        styles.selectList,
        menuState.direction === "up" ? styles.selectListUp : styles.selectListDown
      )}>
      <ScrollHint darkMode={currentIsDark} maxHeight={menuState.maxHeight}>
        {options.map((item) => {
          const selected = value.some((selectedItem) => selectedItem.id === item.id);
          return (
            <Listbox.Option
              key={item.id}
              value={item}
              disabled={item.isDisabled}
              className={({ active, disabled }) =>
                clsx(
                  optionClasses,
                  styles.selectItem,
                  selected &&
                    (currentIsDark ? "bg-gray-700 text-white" : "bg-primary/10 text-primary"),
                  active && !disabled && (currentIsDark ? "bg-gray-700/80" : "bg-gray-100"),
                  disabled && "cursor-not-allowed",
                  disabled && (currentIsDark ? "text-gray-500" : "text-gray-400")
                )
              }>
              <div className="pointer-events-none flex items-center gap-2">
                <Checkbox checked={selected} onChange={() => {}} />
                <span>{item.label}</span>
              </div>
            </Listbox.Option>
          );
        })}
      </ScrollHint>
    </Listbox.Options>
  );

  const portalContent = (
    <Portal>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: PORTAL_Z_INDEX
        }}>
        <div
          style={{
            position: "absolute",
            left: menuState.left,
            top: menuState.direction === "down" ? menuState.top : menuState.top,
            transform: menuState.direction === "up" ? "translateY(-100%)" : undefined,
            width: menuState.width,
            pointerEvents: "auto"
          }}>
          {renderOptions()}
        </div>
      </div>
    </Portal>
  );

  const inlineContent = (
    <div
      className={clsx(
        styles.inlineMenu,
        menuState.direction === "up" ? styles.inlineMenuUp : styles.inlineMenuDown
      )}>
      {renderOptions()}
    </div>
  );

  return (
    <div style={style} className={clsx(styles.selectContainer, className)}>
      {label && (
        <label
          htmlFor={id}
          className={clsx(styles.selectLabel, labelClass, getClassByVariant(color)?.label)}>
          {label}
        </label>
      )}

      <Listbox
        value={value}
        onChange={onChange}
        as="div"
        multiple
        className={clsx(styles.selectWrapper)}
        disabled={disabled}>
        {({ open }) => {
          useLayoutEffect(() => {
            if (open) updateMenuMetrics();
          }, [open, updateMenuMetrics]);

          useEffect(() => {
            if (wasDropdownOpenRef.current && !open) {
              if (onDropdownClose && value.length > 0) {
                const initialValueString = stringifyOptions(initialValues);
                const currentValueString = stringifyOptions(value);

                if (initialValueString !== currentValueString) {
                  onDropdownClose(value);
                }
              }
            }

            wasDropdownOpenRef.current = open;

            if (!open) return;

            const handler = () => updateMenuMetrics();
            window.addEventListener("resize", handler);
            window.addEventListener("scroll", handler, true);

            return () => {
              window.removeEventListener("resize", handler);
              window.removeEventListener("scroll", handler, true);
            };
          }, [open, updateMenuMetrics, value, onDropdownClose]);

          useEffect(() => {
            if (open && !wasDropdownOpenRef.current) {
              setInitialValues([...value]);
            }
          }, [open, value]);

          return (
            <>
              <Listbox.Button
                id={id}
                ref={buttonRef}
                onClick={(e) => {
                  e.stopPropagation(); // Stop event from bubbling up
                  e.nativeEvent.stopImmediatePropagation(); // More aggressive stop
                }}
                className={clsx(
                  styles.selectButton,
                  getClassByVariant(color)?.input,
                  btnClass,
                  inputClass,
                  {
                    "cursor-not-allowed bg-inputDark": disabled,
                    "!text-placeholder": value.length === 0
                  }
                )}>
                <div className={styles.selectedSummary}>{selectionSummary}</div>
                <Icon path={mdiChevronDown} className={clsx(styles.selectArrow, arrowClass)} />
              </Listbox.Button>

              {open && (usePortal ? portalContent : inlineContent)}
            </>
          );
        }}
      </Listbox>
    </div>
  );
}
